import { EventEmitter } from 'events';
import ga from 'react-ga';
import { CargoType } from 'types';
import Dispatcher from 'core/Dispatcher';
import Actions from 'constants/Actions';
import CargosStore from 'stores/CargosStore';
import { validate } from 'api';
import CargosConstants from 'constants/Cargos';
import CategoryStore from 'stores/CategoryStore';
import _ from 'lodash';
import { translate as t } from 'lang';

const CHANGE_EVENT = 'change';

/**
 * Редактируемый груз.
 * Для редактирования груза открывается модальное окно со списком
 * редактируемых аттрибутов. При сохранении изменений редактируемый груз
 * перемещается в список грузов.
 * @type {?CargoType}
 */
var _editingCargo = null;
var _isSecondEditing = false;

/**
 * Some recommendation notices for tankmen
 * 
 * @type {boolean}
 * @private
 */
var _stuffTankmenValidation = true;

/**
 * Начинает редактирование одного из грузов.
 * @param {CargoType} cargo
 */
function startEditing(cargo, isSecondEditing = false) {
    _editingCargo = cargo;
    _isSecondEditing = isSecondEditing;
}

function stopEditing() {

}

/**
 * Сохраняет новое значение аттрибута груза.
 * @param {Object} options
 * @param {string} options.key
 * @param {*} options.value
 */
function updateCargo(payload) {
    var attributes = _editingCargo.attributes.withMutations((attributes) => {

        for (let key in payload) {
            let value = payload[key];

            if (key === 'name') {
                _editingCargo = _editingCargo.set('name', value);
            }

            let prevValue = attributes.getIn([key, 'value']);

            attributes.setIn([key, 'value'], value);

            if (value !== prevValue) {
                attributes.deleteIn([key, 'error']);
            }
        }
    });
    _editingCargo = _editingCargo.set('attributes', attributes);
}

function updateCargoId(newId) {
    var cargo = _editingCargo.set('id', newId);
    _editingCargo = cargo;
}

function sendToGa() {
    var unit_of_measurement = _.find(_editingCargo.attributes.toJS(), function(attribute) {
        return attribute.name === 'unit_of_measurement';
    });

    if (unit_of_measurement && !unit_of_measurement.value) {
        unit_of_measurement.value = [CargosConstants.UNITS.CM];
    }

    var selectedCategory = CategoryStore.getSelected();

    _editingCargo.attributes.forEach(attribute => {

        switch(attribute.name) {
            case 'unit_of_measurement':

                ga.event({
                    category: 'fields',
                    action: unit_of_measurement.value[0] === CargosConstants.UNITS.M ? 'm_t' : 'sm_kg',
                    label: selectedCategory.name,
                    value: 1,
                });
                break;
            case 'name':
                ga.event({
                    category: 'fields',
                    action: attribute.name,
                    label: selectedCategory.name,
                    value: 1,
                });
                break;
            default:
                if (attribute.value) {

                    var value = parseInt(attribute.value, 10);

                    if (isNaN(value)) {
                        value = 1;
                    }

                    if (unit_of_measurement && unit_of_measurement.value[0] === CargosConstants.UNITS.M) {
                        switch (attribute.name) {
                            case 'width':
                            case 'height':
                            case 'length':
                                value = value * 100;
                                break;
                            case 'weight':
                                value = value * 1000;
                                break;
                        }
                    }

                    if (attribute.name === 'volume') {
                        value = value * 1000000;
                    }

                    ga.event({
                        category: 'fields',
                        action: attribute.name,
                        label: selectedCategory.name,
                        value: value,
                    });
                }
                break;
        }
    });
}

class EditingCargoStore extends EventEmitter {

    /**
     * Возвращает редактируемый груз.
     * @return {?CargoType}
     */
    getEditingCargo() {
        return _editingCargo;
    }

    /**
     * Возвращает является ли груз редактируемым(true) или впервые добавляемым(false).
     * @return {?CargoType}
     */
    getIsSecondEditing() {
        return _isSecondEditing;
    }

    /**
     * Проверяет, редактируется ли груз в данный момент.
     * @return {boolean}
     */
    isEditing() {
        return _editingCargo !== null;
    }
    
    isStuffTankmenValidationOn() {
        return _stuffTankmenValidation === true;
    }

    getNormalizedState() {
        return CargosStore.normalizeCargo(_editingCargo);
    }

    validate() {
        return validate().then(
            response => {
                const { stuff } = response;
                if (!stuff) {
                    return Promise.resolve();
                }

                const validationErrors = stuff[0].attributes || {};
                if (stuff[0].name) {
                    validationErrors[0] = stuff[0].name;
                }
                var errorTips = {};
                var expectedValues = {};
                var expectedValuesUnits = {};
                _editingCargo.attributes.forEach(attribute => {
                    let attributeError = validationErrors[attribute.id];
                    if (attributeError) {

                        // Custom error
                        if (attributeError.indexOf('CM_EXPECTED') >= 0 ||
                            attributeError.indexOf('M_EXPECTED') >= 0 ||
                            attributeError.indexOf('KG_EXPECTED') >= 0) {

                            let split = attributeError.split('_');

                            if (split[0] && split[1]) {
                                expectedValues[attribute.name] = split[0];
                                expectedValuesUnits[attribute.name] = split[1];
                                attributeError = t('UNITS_TOO_LARGE_VALUE_PERHAPS_YOU_MEAN') + ' ' + split[0] + ' ' + t(split[1]);
                            }
                        }

                        errorTips[attribute.name] = attributeError;
                    }
                });

                var attributes = _editingCargo.attributes.withMutations((attributes) => {
                    for (let wrongAttribute in errorTips) {
                        attributes = attributes.setIn(
                            [wrongAttribute, 'error'],
                            errorTips[wrongAttribute]
                        );

                        if (expectedValues[wrongAttribute]) {

                            let expectedUnitsValue = expectedValuesUnits[wrongAttribute];
                            if (expectedUnitsValue) {

                                let unitsId = null;
                                switch (expectedUnitsValue) {
                                    case 'KG':
                                        expectedUnitsValue = 'CM';
                                        break;
                                    case 'T':
                                        expectedUnitsValue = 'M';
                                        break;
                                    default:
                                        break;
                                }

                                unitsId = CargosConstants.UNITS[expectedUnitsValue];
                                if (unitsId) {
                                    attributes = attributes.setIn(
                                        ['unit_of_measurement', 'value'],
                                        [unitsId]
                                    );
                                }
                            }
                            
                            attributes = attributes.setIn(
                                [wrongAttribute, 'value'],
                                expectedValues[wrongAttribute]
                            );
                        }
                    }
                });

                _editingCargo = _editingCargo.set('attributes', attributes);
                return Promise.reject(errorTips);
            }
        );
    }

    /**
     * @param {function} callback
     */
    addChangeListener(callback) {
        this.on(CHANGE_EVENT, callback);
    }

    /**
     * @param {function} callback
     */
    removeChangeListener(callback) {
        this.removeListener(CHANGE_EVENT, callback);
    }

    emitChange() {
        this.emit(CHANGE_EVENT);
    }

    dispatchToken = Dispatcher.register((action) => {
        const payload = action.payload;

        switch (action.type) {
            case Actions.ADD_CARGO:
                Dispatcher.waitFor([CargosStore.dispatchToken]);
                startEditing(payload.cargo);
                break;

            case Actions.START_CARGO_EDITING:
                startEditing(payload.cargo, payload.isSecondEditing);
                break;

            case Actions.STOP_CARGO_EDITING:
                Dispatcher.waitFor([CargosStore.dispatchToken]);
                _editingCargo = null;
                break;

            case Actions.CANCEL_CARGO_EDITING:
                _editingCargo = null;
                break;

            case Actions.UPDATE_EDITING_CARGO:
                updateCargo(payload);
                break;

            case Actions.UPDATE_EDITING_CARGO_ID:
                updateCargoId(payload.newCargoId);
                break;

            case Actions.VALIDATE_CARGO:
                this.validate();
                break;

            case Actions.SEND_ATTRIBUTES_TO_GA:
                sendToGa();
                break;
            
            case Actions.CANCEL_TANKMEN_VALIDATION:
                _stuffTankmenValidation = false;
                break;

            case Actions.ENABLE_TANKMEN_VALIDATION:
                _stuffTankmenValidation = true;
                break;
            
            case Actions.SHOW_APPROXIMATE_UNITS_WARNING:
                _editingCargo = _editingCargo.set('show_approximate_units_warning', true);
                break;

            case Actions.HIDE_APPROXIMATE_UNITS_WARNING:
                _editingCargo = _editingCargo.set('show_approximate_units_warning', false);
                break;

            default:
                return;
        }

        this.emitChange();
    })

}

export default new EditingCargoStore();



// WEBPACK FOOTER //
// ./client/stores/EditingCargoStore.js